import { create } from 'zustand';
import { supabase } from '../lib/client';

interface User {
  id: string;
  auth_id: string | null;
  organization_id: string | null;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: string;
  preferences: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  setUser: (user) => set({ user, loading: false }),
  setLoading: (loading) => set({ loading }),
  signOut: () => set({ user: null, loading: false }),
}));

let initialized = false;

async function fetchUserByAuthId(authId: string): Promise<User | null> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('auth_id', authId)
    .single();
  
  if (data && !error) {
    return data as User;
  }
  return null;
}

export async function initializeAuth() {
  if (initialized) return;
  initialized = true;

  const timeoutMs = 15000;

  const initPromise = (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Auth init: session found:', !!session);
      
      if (session?.user) {
        const userData = await fetchUserByAuthId(session.user.id);
        console.log('Auth init: user data:', userData?.organization_id);
        
        if (userData) {
          useAuthStore.getState().setUser(userData);
          return;
        }
      }
      useAuthStore.getState().setLoading(false);
    } catch (error) {
      console.warn('Auth initialization failed:', error);
      useAuthStore.getState().setLoading(false);
    }
  })();

  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(() => {
      console.warn('Auth initialization timed out');
      useAuthStore.getState().setLoading(false);
      resolve();
    }, timeoutMs);
  });

  await Promise.race([initPromise, timeoutPromise]);

  supabase.auth.onAuthStateChange(async (event, session) => {
    console.log('Auth state change:', event, !!session?.user);
    
    if (event === 'SIGNED_IN' && session?.user) {
      const userData = await fetchUserByAuthId(session.user.id);
      console.log('Signed in user:', userData?.organization_id);
      
      if (userData) {
        useAuthStore.getState().setUser(userData);
      } else {
        useAuthStore.getState().setLoading(false);
      }
    } else if (event === 'SIGNED_OUT') {
      useAuthStore.getState().setUser(null);
      useAuthStore.getState().setLoading(false);
    }
  });
}

export function useAuth() {
  const { user, loading, setUser, signOut } = useAuthStore();
  
  return {
    user,
    loading,
    setUser,
    signOut,
    isAuthenticated: !!user && !!user.organization_id,
  };
}
